import { X, RefreshCw, ExternalLink, Clock, Activity, ShieldAlert, Wrench } from 'lucide-react';
import { useTenantDetail } from '../hooks/useTenants.ts';
import type { TenantDetail } from '@/shared/types/tenant.ts';

interface Props {
  tenantId: string;
  onClose: () => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ProbeStatusBadge({ resultado }: { resultado: 'UP' | 'DOWN' }) {
  if (resultado === 'UP') {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
        style={{ background: 'var(--color-status-up)' }}
      >
        UP
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
      style={{ background: 'var(--color-status-down)' }}
    >
      DOWN
    </span>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{children}</span>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-400">{icon}</span>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">{title}</h4>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">{children}</div>
    </div>
  );
}

function DetailContent({ detail }: { detail: TenantDetail }) {
  const { lastProbe, uptimeMonth } = detail;

  return (
    <div className="space-y-6 px-6 py-5">
      {/* Informações gerais */}
      <Section icon={<Activity size={16} />} title="Informações Gerais">
        <DetailRow label="Nome">{detail.nome}</DetailRow>
        <DetailRow label="Slug">
          <span className="font-mono text-xs">{detail.slug}</span>
        </DetailRow>
        <DetailRow label="URL Probe">
          <a
            href={detail.urlProbe}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-xs text-blue-600 hover:underline dark:text-blue-400"
          >
            {detail.urlProbe}
            <ExternalLink size={12} />
          </a>
        </DetailRow>
        <DetailRow label="Status">
          <div className="flex items-center gap-1.5">
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                detail.isActive
                  ? 'text-white'
                  : 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
              }`}
              style={detail.isActive ? { background: 'var(--color-status-up)' } : undefined}
            >
              {detail.isActive ? 'Ativo' : 'Inativo'}
            </span>
            {detail.probePaused && (
              <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                Pausado
              </span>
            )}
          </div>
        </DetailRow>
        <DetailRow label="Criado em">{fmtDate(detail.createdAt)}</DetailRow>
        <DetailRow label="Atualizado em">{fmtDate(detail.updatedAt)}</DetailRow>
      </Section>

      {/* Último Probe */}
      <Section icon={<Clock size={16} />} title="Último Probe">
        {lastProbe ? (
          <>
            <DetailRow label="Resultado">
              <ProbeStatusBadge resultado={lastProbe.resultado} />
            </DetailRow>
            <DetailRow label="Verificado em">{fmtDate(lastProbe.checkedAt)}</DetailRow>
            {lastProbe.statusCode != null && (
              <DetailRow label="Status HTTP">
                <span className="font-mono">{lastProbe.statusCode}</span>
              </DetailRow>
            )}
            {lastProbe.responseTimeMs != null && (
              <DetailRow label="Latência">
                <span className="font-mono">{lastProbe.responseTimeMs} ms</span>
              </DetailRow>
            )}
            {lastProbe.errorMessage && (
              <DetailRow label="Erro">
                <span className="text-xs text-red-600 dark:text-red-400">{lastProbe.errorMessage}</span>
              </DetailRow>
            )}
          </>
        ) : (
          <p className="py-2 text-sm text-gray-400">Nenhum probe registrado</p>
        )}
      </Section>

      {/* Uptime do Mês */}
      <Section icon={<ShieldAlert size={16} />} title="Uptime do Mês Atual">
        {uptimeMonth ? (
          <>
            <DetailRow label="Uptime">
              <span
                className="font-mono font-semibold"
                style={{ color: Number(uptimeMonth.uptimePercent) >= 99 ? 'var(--color-status-up)' : 'var(--color-status-down)' }}
              >
                {uptimeMonth.uptimePercent}%
              </span>
            </DetailRow>
            <DetailRow label="Minutos indisponíveis">
              <span className="font-mono">{uptimeMonth.minutosIndisponiveis}</span>
            </DetailRow>
            <DetailRow label="Incidentes">
              <span className="font-mono">{uptimeMonth.totalIncidentes}</span>
            </DetailRow>
            <DetailRow label="Manutenções">
              <div className="flex items-center gap-1.5">
                <Wrench size={13} className="text-gray-400" />
                <span className="font-mono">{uptimeMonth.totalManutencoes}</span>
                {uptimeMonth.minutosManutencao > 0 && (
                  <span className="text-xs text-gray-400">({uptimeMonth.minutosManutencao} min)</span>
                )}
              </div>
            </DetailRow>
          </>
        ) : (
          <p className="py-2 text-sm text-gray-400">Sem dados de uptime para o mês atual</p>
        )}
      </Section>
    </div>
  );
}

export function TenantDetailModal({ tenantId, onClose }: Props) {
  const { data: detail, isLoading } = useTenantDetail(tenantId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-900">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
          <h2 className="text-lg font-semibold">Detalhes do Tenant</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={20} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : detail ? (
          <DetailContent detail={detail} />
        ) : (
          <div className="flex h-48 items-center justify-center text-sm text-gray-400">
            Tenant não encontrado
          </div>
        )}
      </div>
    </div>
  );
}
